import { type TimelineFeatures, useFeatures } from "@expert/features";
import type { GaiaSubscription, GaiaWebsocketContext } from "@expert/gaia";
import { useGaiaWebsocket } from "@expert/gaia";
import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import { useFetchOutageInfo } from "@expert/solve-tools";
import { usePrevious } from "@mantine/hooks";
import {
    addFirstOpenMessageIfNeeded,
    addSeparatorMessage,
    formatTimestamp,
    solveSessionClosed,
    solveSessionStarted,
} from "@soluto-private/expert-workspace-timeline";
import pRetry from "p-retry";
import { useEffect, useMemo, useState } from "react";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import {
    getIsIdleSession,
    isSimulatedVoiceTask,
    sdkEventBus,
    useActiveTask,
    useAgentFriendlyName,
    usePartner,
    useSession,
    type VoiceTask,
} from "@/sdk";
import { userCache } from "@/shared-utils";
import { formatTime } from "@/shared-utils/formatters/formatTime";

const subscriptionTimeoutDuration = 2000;
const subscriptionRetryCount = 3;

interface SessionLifecycleProps {
    features: Pick<TimelineFeatures, "isProactiveEnabled">;
    partner: Partner;
    sessionId: string;
    websocketObj: GaiaWebsocketContext | null;
    withCustomer: boolean;
    logger: Logger;
    callSid?: string;
    explicitSubscriptions?: string[];
}

type SubscribeProps = {
    websocketObj: GaiaWebsocketContext | null;
} & Omit<GaiaSubscription, "sendJsonMessage" | "timeout">;

export const useVoiceExpertAssistSessions = (logger: Logger) => {
    const [currentSolveSessionId, setCurrentSolveSessionId] = useState<string>();
    const { id: sessionId } = useSession();
    const partner = usePartner();
    const agentName = useAgentFriendlyName();
    const { features } = useFeatures<TimelineFeatures>();
    const { isRealtimeTranscriptEnabled } = usePartnerFeatureConfigured();
    const explicitSubscriptions = isRealtimeTranscriptEnabled ? ["transcripts"] : undefined;
    const websocketObj = useGaiaWebsocket();
    const task = useActiveTask();
    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(task) ? task : null;
    }, [task]);
    const { fetchOutageInfo } = useFetchOutageInfo(logger);

    // TODO(afd): Revisit this to properly handle non-voice tasks and not unsafely cast this

    const callSid = (task as VoiceTask)?.agentCallId;
    const previousCallSid = usePrevious(callSid);
    const previousWSLoading = usePrevious(websocketObj?.loading);
    const ssoAccessToken = userCache.ssoAccessToken ?? "";

    /**
     * Set-up session lifecycle hooks for call start and call end events
     */

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const sessionChangedSubscription = sdkEventBus.on("session_changed", ({ action, session }) => {
            if (action === "start") {
                setCurrentSolveSessionId(session.id);

                void startSession({
                    callSid,
                    features,
                    partner,
                    sessionId: session.id,
                    websocketObj,
                    withCustomer: session.kind === "with-customer",
                    logger,
                    explicitSubscriptions,
                });
            }

            if (action === "end") {
                closeSession({ sessionId: session.id, websocketObj, callSid, partner, features, logger });
                setCurrentSolveSessionId(undefined);
                addSeparatorMessage(session.id, {
                    text: "Call ended",
                    timestamp: formatTimestamp(),
                    callDuration: formatTime(
                        session.startedTimestamp ? Math.floor((Date.now() - session.startedTimestamp) / 1000) : 0,
                        true,
                    ),
                });
            }
        });

        return () => sessionChangedSubscription();
    }, []);

    useEffect(() => {
        addFirstOpenMessageIfNeeded(sessionId, agentName);
    }, [agentName, sessionId]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (sessionId && features.isOutageAlertInfoEnabled && ssoAccessToken) {
            void fetchOutageInfo(ssoAccessToken);
        }
    }, [sessionId, features.isOutageAlertInfoEnabled, ssoAccessToken]);

    /**
     * Handle session start and resubscribe to GAIA when callSid changes
     */

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!sessionId || !websocketObj || websocketObj.loading) return;

        const callSidChanged = callSid && previousCallSid !== callSid;
        const websocketObjJustLoaded = previousWSLoading === true;
        const withCustomer = getIsIdleSession() === false;

        /** When a call comes, we subscribe to GAIA with a sessionId (callSid is undefined).
         * Once the callSid arrives, we need to resubscribe to GAIA with the existing sessionId and new callSid **/
        if (callSidChanged) {
            if (activeExpertUniversityTask) {
                setCurrentSolveSessionId(sessionId);
                solveSessionStarted({ sessionId, callSid, withCustomer, partner, features });
            }

            void subscribeSessionToGaia({ sessionId, partner, websocketObj, callSid, logger, explicitSubscriptions });
            return;
        }

        // On initial load, we must wait for websocket to finish loading before handling session start
        // when websocket cycles through ready states on reconnect, we should resubscribe session to gaia
        if (websocketObjJustLoaded) {
            if (currentSolveSessionId !== sessionId) {
                setCurrentSolveSessionId(sessionId);
                solveSessionStarted({ sessionId, callSid, withCustomer, partner, features });
            }
            void subscribeSessionToGaia({ sessionId, callSid, websocketObj, partner, logger, explicitSubscriptions });
        }
    }, [callSid, sessionId, websocketObj?.loading]);
};

const startSession = async ({
    sessionId,
    websocketObj,
    withCustomer,
    callSid,
    partner,
    features,
    logger,
    explicitSubscriptions,
}: SessionLifecycleProps) => {
    solveSessionStarted({ sessionId, callSid, withCustomer, partner, features });
    await subscribeSessionToGaia({ sessionId, callSid, websocketObj, partner, logger, explicitSubscriptions });
};

const subscribeSessionToGaia = async ({
    sessionId,
    callSid,
    partner,
    websocketObj,
    logger,
    explicitSubscriptions,
}: SubscribeProps) => {
    try {
        await pRetry(
            async () => {
                await websocketObj?.subscribeSessionToGaia({
                    sessionId,
                    callSid,
                    partner,
                    sendJsonMessage: websocketObj.sendJsonMessage,
                    timeout: subscriptionTimeoutDuration,
                    logger,
                    explicitSubscriptions,
                });
            },
            { minTimeout: subscriptionTimeoutDuration, factor: 1, retries: subscriptionRetryCount },
        );
    } catch (error) {
        logger.error(
            { error, module: "useVoiceExpertAssistSessions" },
            `Error subscribing session ${sessionId} to gaia with max retries (${subscriptionRetryCount})`,
        );
    }
};

const closeSession = ({
    callSid,
    features,
    logger,
    partner,
    sessionId,
    websocketObj,
}: Omit<SessionLifecycleProps, "withCustomer">) => {
    solveSessionClosed({ sessionId, callSid, partner, features });
    websocketObj?.unsubscribeSessionFromGaia({
        logger,
        sessionId,
        sendJsonMessage: websocketObj.sendJsonMessage,
    });
};
